<template>
  <div :class="[bgColor]">
    <section
      class="mx-auto max-w-[90rem] space-y-10"
      :class="[verticalTopSpacing, verticalBottomSpacing]"
    >
      <div class="space-y-10 px-6 lg:px-20">
        <BaseSectionHeader
          :title="sectionHeader.title"
          :tagline="sectionHeader.tagline"
          :alignment="sectionHeader.alignment"
          :paragraphs="sectionHeader.paragraphs"
          :primary-button="sectionHeader.primaryButton"
          :secondary-button="sectionHeader.secondaryButton"
          :variant="sectionHeader.variant"
          :section-intro="sectionHeader.sectionIntro"
        ></BaseSectionHeader>

        <section
          class="hidden gap-4 sm:grid sm:grid-cols-2"
          :class="{
            'xl:grid-cols-2': cards.length < 3,
            'xl:grid-cols-3': cards.length == 3,
            'xl:grid-cols-4': cards.length > 4,
          }"
        >
          <template v-for="(card, index) in cards" :key="index">
            <BaseCard
              :title="card.title"
              :text="card.text"
              :image="card.image"
              :variant="cardsColor"
              :button="card.button"
            ></BaseCard>
          </template>
        </section>
      </div>

      <section class="relative sm:hidden">
        <swiper-container
          ref="swiperRef"
          slides-per-view="auto"
          init="false"
          :loop="false"
          :keyboard="true"
          class="pl-6"
        >
          <swiper-slide v-for="card in cards" :key="card" class="mr-3 h-auto w-[21.375rem]">
            <BaseCard
              :title="card.title"
              :text="card.text"
              :image="card.image"
              :variant="cardsColor"
              :button="card.button"
            ></BaseCard>
          </swiper-slide>
        </swiper-container>

        <button
          class="absolute left-1 top-1/2 z-10 -translate-y-1/2 transform rounded-full border border-border-light p-3.5"
          :class="[
            isAtStart ? 'pointer-events-none bg-white text-text-dark' : 'bg-text-accent text-white',
          ]"
          @click="prevSlide"
        >
          <BaseIcon icon="ArrowLeft" class="size-6" />
        </button>

        <button
          class="absolute right-5 top-1/2 z-10 -translate-y-1/2 transform rounded-full border border-border-light p-3.5"
          :class="[
            isAtEnd ? 'pointer-events-none bg-white text-text-dark' : 'bg-text-accent text-white',
          ]"
          @click="nextSlide"
        >
          <BaseIcon icon="ArrowRight" class="size-6" />
        </button>
      </section>
    </section>
  </div>
</template>

<script setup lang="ts">
import BaseSectionHeader from '@/common/components/base/BaseSectionHeader/BaseSectionHeader.vue'
import type { IBlockCardImages } from '@/features/block-card-images/types/IBlockCardImages.ts'
import BaseCard from '@/common/components/base/BaseCard/BaseCard.vue'
import { computed, onMounted, ref } from 'vue'
import { register } from 'swiper/element'
import type { SwiperOptions } from 'swiper/types'
import type Swiper from 'swiper'
import BaseIcon from '@/common/components/base/BaseIcon/BaseIcon.vue'
import { getBgColor } from '@/common/utils/getBgColor.ts'

const props = defineProps<IBlockCardImages>()

const bgColor = computed(() => getBgColor(props.variant))

//#region carousel mobile
register()

const isAtStart = ref(true)
const isAtEnd = ref(false)
const swiperRef = ref<Swiper | null>(null)
const activeIndex = ref(0)

const getSwiper = (): Swiper | null => {
  return swiperRef.value ? (swiperRef.value as any).swiper : null
}

const prevSlide = () => {
  const swiper = getSwiper()
  if (swiper) {
    swiper.slidePrev()
    updateButtonState(swiper)
  }
}

const nextSlide = () => {
  const swiper = getSwiper()
  if (swiper) {
    swiper.slideNext()
    updateButtonState(swiper)
  }
}

const updateButtonState = (swiper: Swiper) => {
  isAtStart.value = swiper.isBeginning
  isAtEnd.value = swiper.isEnd
  activeIndex.value = swiper.activeIndex
  if (swiper.isEnd) {
    activeIndex.value = swiper.slides.length - 1
  }
}

onMounted(() => {
  const swiperEl: any = swiperRef.value
  if (swiperEl) {
    const params: SwiperOptions = {
      on: {
        init: (swiper: Swiper) => {
          updateButtonState(swiper)
        },
        slideChange: (swiper: Swiper) => {
          updateButtonState(swiper)
        },
      },
    }

    Object.assign(swiperEl, params)
    swiperEl.initialize()
  }
})
//#endregion
</script>
